import { useState, useEffect, useMemo, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import CheckInOutNote from "@/components/feature/CheckInOutNote";
import {
  getVillaAvailability,
  getVillaBlockedDates,
  createVillaReservationViaEdge,
  type EdgeReservationResponse,
} from "@/lib/villaApi";
import { parseISODate, toISODate } from "@/components/base/DatePicker";
import RangeDatePicker from "@/components/base/RangeDatePicker";
import NationalitySelect from "@/components/base/NationalitySelect";
import { img } from "@/lib/images";
import { LocalizedLink } from "@/i18n/language";
import { usePriceMap } from "@/hooks/usePrices";

type Status = { type: "idle" | "sending" | "success" | "error"; msg: string };

type Availability = {
  reservations: { check_in: string; check_out: string }[];
  blockedDates: string[];
};

const WHOLE_PROPERTY_SLUG = "villa-gesamt";

const VILLAS = [
  { slug: "villa-frangipani", nameDe: "Villa Frangipani", nameEn: "Villa Frangipani" },
  { slug: "villa-bougainvillea", nameDe: "Villa Bougainvillea", nameEn: "Villa Bougainvillea" },
  { slug: "villa-hibiskus", nameDe: "Villa Hibiskus", nameEn: "Villa Hibiscus" },
  { slug: WHOLE_PROPERTY_SLUG, nameDe: "Gesamtes Anwesen (alle 3 Villen)", nameEn: "Entire property (all 3 villas)" },
];

// Slugs whose bookings/blocked dates make the selected option unavailable.
// The whole-property option is blocked by every villa, and each single villa is
// blocked by a whole-property booking.
const ALL_AVAILABILITY_SLUGS = [
  "villa-frangipani",
  "villa-bougainvillea",
  "villa-hibiskus",
  WHOLE_PROPERTY_SLUG,
];

const BLOCKING_SOURCES: Record<string, string[]> = {
  "villa-frangipani": ["villa-frangipani", WHOLE_PROPERTY_SLUG],
  "villa-bougainvillea": ["villa-bougainvillea", WHOLE_PROPERTY_SLUG],
  "villa-hibiskus": ["villa-hibiskus", WHOLE_PROPERTY_SLUG],
  [WHOLE_PROPERTY_SLUG]: ["villa-frangipani", "villa-bougainvillea", "villa-hibiskus", WHOLE_PROPERTY_SLUG],
};

const ROOM_OPTIONS: Record<string, { rooms: number }[]> = {
  "villa-frangipani": [{ rooms: 1 }, { rooms: 2 }],
  "villa-hibiskus": [{ rooms: 2 }, { rooms: 3 }],
};

// Ordnet Villa + Zimmerauswahl dem Backstage-Preis-Schlüssel zu, damit der
// angezeigte Preis immer den im Backstage verwalteten Werten (inkl. Euro) entspricht.
function priceKeyFor(villaSlug: string, rooms: number): string {
  if (villaSlug === "villa-bougainvillea") return "villa_bougainvillea_1";
  if (villaSlug === "villa-frangipani") return rooms === 2 ? "villa_frangipani_2" : "villa_frangipani_1";
  if (villaSlug === "villa-hibiskus") return rooms === 3 ? "villa_hibiskus_3" : "villa_hibiskus_2";
  if (villaSlug === WHOLE_PROPERTY_SLUG) return "villa_gesamt";
  return "";
}

function getMaxAdults(villaSlug: string, rooms: number): number {
  if (villaSlug === WHOLE_PROPERTY_SLUG) return 12;
  if (villaSlug === "villa-frangipani") return rooms === 2 ? 4 : 2;
  if (villaSlug === "villa-bougainvillea") return 2;
  if (villaSlug === "villa-hibiskus") return rooms === 3 ? 6 : 4;
  return 20;
}

export default function VillaReservationPage() {
  const { i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const { priceMap, money } = usePriceMap();
  const [searchParams] = useSearchParams();

  const [villaSlug, setVillaSlug] = useState(
    searchParams.get("villa") || "villa-frangipani"
  );
  const [checkIn, setCheckIn] = useState(searchParams.get("checkin") || "");
  const [checkOut, setCheckOut] = useState(searchParams.get("checkout") || "");
  const [adults, setAdults] = useState(2);
  const [rooms, setRooms] = useState(1);
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });

  const [rawAvailability, setRawAvailability] = useState<Record<string, Availability>>({});

  const maxAdults = getMaxAdults(villaSlug, rooms);

  // Prefill villa and dates coming from the availability overview.
  useEffect(() => {
    const villaParam = searchParams.get("villa");
    const checkinParam = searchParams.get("checkin");
    const checkoutParam = searchParams.get("checkout");
    if (villaParam) setVillaSlug(villaParam);
    if (checkinParam) setCheckIn(checkinParam);
    if (checkoutParam) setCheckOut(checkoutParam);
  }, [searchParams]);

  // Clamp occupancy whenever the villa or number of rooms changes.
  useEffect(() => {
    setAdults((a) => Math.min(a, maxAdults));
  }, [maxAdults]);

  // Reset rooms to a valid option whenever the villa changes.
  useEffect(() => {
    const opts = ROOM_OPTIONS[villaSlug];
    if (opts && opts.length > 0) {
      setRooms((prev) => (opts.some((o) => o.rooms === prev) ? prev : opts[0].rooms));
    } else {
      setRooms(1);
    }
  }, [villaSlug]);

  const today = new Date().toISOString().split("T")[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);
  const maxDateStr = maxDate.toISOString().split("T")[0];

  useEffect(() => {
    const load = async () => {
      try {
        const entries = await Promise.all(
          ALL_AVAILABILITY_SLUGS.map(async (slug) => {
            const [resRes, blockRes] = await Promise.all([
              getVillaAvailability(slug),
              getVillaBlockedDates(slug),
            ]);
            const value: Availability =
              resRes.error || blockRes.error
                ? { reservations: [], blockedDates: [] }
                : {
                    reservations: (resRes.data ?? []) as { check_in: string; check_out: string }[],
                    blockedDates: ((blockRes.data ?? []) as { blocked_date: string }[]).map(
                      (d) => d.blocked_date
                    ),
                  };
            return [slug, value] as const;
          })
        );
        setRawAvailability(Object.fromEntries(entries));
      } catch {
        setRawAvailability({});
      }
    };
    load();
  }, []);

  // Merge availability: the whole-property option occupies/blocks every villa, and a
  // whole-property booking makes each individual villa unavailable.
  const availability = useMemo<Availability>(() => {
    const sources = BLOCKING_SOURCES[villaSlug] ?? [villaSlug];
    const reservations: { check_in: string; check_out: string }[] = [];
    const blockedDates: string[] = [];
    for (const s of sources) {
      const a = rawAvailability[s];
      if (!a) continue;
      reservations.push(...a.reservations);
      blockedDates.push(...a.blockedDates);
    }
    return { reservations, blockedDates };
  }, [rawAvailability, villaSlug]);

  const disabledDates = useMemo(() => {
    const set = new Set<string>();
    for (const d of availability.blockedDates) set.add(d);
    for (const r of availability.reservations) {
      const start = parseISODate(r.check_in);
      const end = parseISODate(r.check_out);
      if (!start || !end) continue;
      const cur = new Date(start);
      while (cur < end) {
        set.add(toISODate(cur));
        cur.setDate(cur.getDate() + 1);
      }
    }
    return set;
  }, [availability]);

  // firstOccupiedNight = the earliest night after checkIn that is already booked/blocked.
  // For blocked dates it's the date itself. For reservations, the first occupied night
  // is the check_in of the next booking (= the turnover day: departing guest leaves, arriving guest comes).
  const firstOccupiedNight = useMemo(() => {
    if (!checkIn) return null;
    const checkInDate = parseISODate(checkIn);
    if (!checkInDate) return null;
    let first: Date | null = null;

    for (const d of availability.blockedDates) {
      const bd = parseISODate(d);
      if (bd && bd > checkInDate && (!first || bd < first)) first = bd;
    }

    for (const r of availability.reservations) {
      const start = parseISODate(r.check_in);
      const end = parseISODate(r.check_out);
      if (!start || !end) continue;
      if (start > checkInDate && (!first || start < first)) first = new Date(start);
      const cur = new Date(start);
      while (cur < end) {
        if (cur > checkInDate && (!first || cur < first)) first = new Date(cur);
        cur.setDate(cur.getDate() + 1);
      }
    }

    return first ? toISODate(first) : null;
  }, [availability, checkIn]);

  // maxCheckout: allow selecting the turnover day (firstOccupiedNight) as departure —
  // that's the half-day checkout. Nothing beyond it is selectable.
  const maxCheckout = useMemo(() => {
    return firstOccupiedNight ?? maxDateStr;
  }, [firstOccupiedNight, maxDateStr]);

  // Departure half-day: the first occupied night after check-in is the turnover day.
  // The current guest departs in the morning, the next guest arrives in the afternoon.
  // Remove it from the visual "booked" set so it appears white/free, matching the arrival half-day.
  const checkoutBookedDates = useMemo(() => {
    const set = new Set(disabledDates);
    if (firstOccupiedNight) set.delete(firstOccupiedNight);
    return set;
  }, [disabledDates, firstOccupiedNight]);

  // Turnover dates for check-in calendar: check_out dates of existing reservations
  const checkInTurnoverDates = useMemo(() => {
    return new Set(availability.reservations.map((r) => r.check_out));
  }, [availability.reservations]);

  // Turnover dates for check-out calendar: the first occupied night (next guest arrives)
  const checkOutTurnoverDates = useMemo(() => {
    if (!firstOccupiedNight) return new Set<string>();
    return new Set([firstOccupiedNight]);
  }, [firstOccupiedNight]);

  // Turnover day for check-in: the selected check-in date equals the check-out date
  // of an existing reservation (another guest is departing that morning).
  const isTurnoverDay = useMemo(() => {
    if (!checkIn) return false;
    const checkInStr = toISODate(parseISODate(checkIn) ?? new Date());
    return availability.reservations.some((r) => {
      const checkOut = parseISODate(r.check_out);
      return checkOut && toISODate(checkOut) === checkInStr;
    });
  }, [availability, checkIn]);

  // Turnover day for check-out: the selected check-out date equals the first occupied night
  const isCheckoutTurnoverDay = useMemo(() => {
    if (!checkOut || !firstOccupiedNight) return false;
    const checkOutStr = toISODate(parseISODate(checkOut) ?? new Date());
    const firstOccupiedStr = toISODate(parseISODate(firstOccupiedNight) ?? new Date());
    return checkOutStr === firstOccupiedStr;
  }, [checkOut, firstOccupiedNight]);

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const a = parseISODate(checkIn);
    const b = parseISODate(checkOut);
    if (!a || !b) return 0;
    return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
  }, [checkIn, checkOut]);

  const pricePerNight = priceMap[priceKeyFor(villaSlug, rooms)] ?? 0;
  const totalPrice = nights > 0 ? nights * pricePerNight : 0;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot =
      (form.elements.namedItem("company_alt") as HTMLInputElement | null)?.value.trim() ?? "";
    if (honeypot) {
      setStatus({ type: "success", msg: isDe ? "Vielen Dank!" : "Thank you!" });
      form.reset();
      setCheckIn("");
      setCheckOut("");
      return;
    }

    if (!checkIn || !checkOut) {
      setStatus({
        type: "error",
        msg: isDe
          ? "Bitte wählen Sie An- und Abreisedatum."
          : "Please select check-in and check-out dates.",
      });
      return;
    }

    if (checkOut <= checkIn) {
      setStatus({
        type: "error",
        msg: isDe
          ? "Das Abreisedatum muss nach dem Anreisedatum liegen."
          : "Check-out date must be after check-in date.",
      });
      return;
    }

    const fd = new FormData(form);
    fd.delete("company_alt");

    const payload: Record<string, string> = {};
    fd.forEach((value, key) => {
      if (typeof value === "string") payload[key] = value;
    });

    if (!payload.firstname?.trim() || !payload.lastname?.trim() || !payload.email?.trim() || !payload.phone?.trim() || !payload.nationality?.trim()) {
      setStatus({
        type: "error",
        msg: isDe
          ? "Bitte füllen Sie alle Pflichtfelder aus (Name, E-Mail, Telefon und Nationalität)."
          : "Please fill in all required fields (name, email, phone and nationality).",
      });
      return;
    }

    const termsChecked =
      (form.elements.namedItem("terms") as HTMLInputElement | null)?.checked ?? false;
    const privacyChecked =
      (form.elements.namedItem("privacy") as HTMLInputElement | null)?.checked ?? false;
    if (!termsChecked || !privacyChecked) {
      setStatus({
        type: "error",
        msg: isDe
          ? "Bitte akzeptieren Sie die AGB und die Datenschutzerklärung."
          : "Please accept the terms and conditions and the privacy policy.",
      });
      return;
    }

    setStatus({ type: "sending", msg: "" });
    try {
      const { data, error } = await createVillaReservationViaEdge({
        villa_slug: villaSlug,
        check_in: checkIn,
        check_out: checkOut,
        adults,
        rooms,
        firstname: payload.firstname,
        lastname: String(payload.lastname || "").toUpperCase(),
        email: payload.email,
        phone: payload.phone || null,
        nationality: payload.nationality || null,
        message: payload.message || null,
        lang: isDe ? "de" : "en",
      });

      if (error) {
        const serverMsg = error.message || "";
        if (serverMsg.includes("already booked") || serverMsg.includes("blocked") || serverMsg.includes("unavailable")) {
          setStatus({
            type: "error",
            msg: isDe
              ? "Diese Daten sind leider nicht mehr verfügbar. Bitte wählen Sie einen anderen Zeitraum."
              : "These dates are unfortunately no longer available. Please choose another period.",
          });
        } else if (serverMsg.includes("Email") || serverMsg.includes("Resend") || serverMsg.includes("mail")) {
          setStatus({
            type: "error",
            msg: (isDe ? "E-Mail-Versand fehlgeschlagen" : "Email sending failed") + ": " + serverMsg,
          });
        } else {
          setStatus({
            type: "error",
            msg: (isDe ? "Etwas ist schiefgelaufen" : "Something went wrong") + " (" + serverMsg + ")",
          });
        }
        return;
      }

      const result = data as EdgeReservationResponse | null;
      if (result && "error" in result) {
        const serverMsg = result.message || result.error || "";
        if (serverMsg.includes("already booked") || serverMsg.includes("blocked") || serverMsg.includes("unavailable")) {
          setStatus({
            type: "error",
            msg: isDe
              ? "Diese Daten sind leider nicht mehr verfügbar. Bitte wählen Sie einen anderen Zeitraum."
              : "These dates are unfortunately no longer available. Please choose another period.",
          });
        } else if (serverMsg.includes("Email") || serverMsg.includes("Resend") || serverMsg.includes("mail")) {
          setStatus({
            type: "error",
            msg: (isDe ? "E-Mail-Versand fehlgeschlagen" : "Email sending failed") + ": " + serverMsg,
          });
        } else {
          setStatus({
            type: "error",
            msg: (isDe ? "Etwas ist schiefgelaufen" : "Something went wrong") + " (" + serverMsg + ")",
          });
        }
        return;
      }

      const emailOk = result?.emailStatus === "sent";
      setStatus({
        type: "success",
        msg: isDe
          ? (emailOk
            ? "Vielen Dank! Ihre Reservierungsanfrage wurde gesendet und eine Bestätigungs-E-Mail wurde versandt."
            : "Vielen Dank! Ihre Reservierungsanfrage wurde gesendet. Wir melden uns innerhalb von 24 Stunden per E-Mail.")
          : (emailOk
            ? "Thank you! Your reservation request has been sent and a confirmation email has been dispatched."
            : "Thank you! Your reservation request has been sent. We will get back to you within 24 hours by email."),
      });
      form.reset();
      setCheckIn("");
      setCheckOut("");
      setAdults(2);
    } catch (err) {
      const detail = err instanceof Error ? err.message : isDe ? "unbekannter Fehler" : "unknown error";
      setStatus({
        type: "error",
        msg: (isDe ? "Etwas ist schiefgelaufen" : "Something went wrong") + " (" + detail + ")",
      });
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors";
  const labelClass = "block text-sm font-medium text-foreground-800 mb-1.5";
  const sectionTitleClass =
    "font-heading text-xl text-foreground-950 mt-8 mb-4 pb-2 border-b border-background-200";

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={isDe ? "Online Reservierung" : "Online Reservation"}
          title={isDe ? "Villa reservieren" : "Reserve a Villa"}
          subtitle={
            isDe
              ? "Reservieren Sie Ihren Aufenthalt in einer unserer Villen. Gebuchte Tage werden automatisch gesperrt — wir bestätigen Ihre Anfrage innerhalb von 24 Stunden per E-Mail."
              : "Reserve your stay in one of our villas. Booked days are automatically blocked — we confirm your request within 24 hours by email."
          }
          image={img("villas/villa-reservation/header-villa-reservation-01.jpg")}
          imageAlt="Villa Kompiang Online Reservation"
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-10">
              <form onSubmit={handleSubmit} noValidate>
                <h2 className={sectionTitleClass}>
                  {isDe ? "Ihr Aufenthalt" : "Your Stay"}
                </h2>

                <div className="space-y-5">
                  {/* Villa */}
                  <div>
                    <label htmlFor="villa" className={labelClass}>
                      {isDe ? "Gewünschte Villa" : "Desired Villa"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <select
                      id="villa"
                      value={villaSlug}
                      onChange={(e) => setVillaSlug(e.target.value)}
                      className={`${inputClass} cursor-pointer`}
                    >
                      {VILLAS.map((v) => (
                        <option key={v.slug} value={v.slug}>
                          {isDe ? v.nameDe : v.nameEn}
                        </option>
                      ))}
                    </select>
                    {villaSlug === WHOLE_PROPERTY_SLUG && (
                      <p className="mt-2 flex items-start gap-2 text-xs text-accent-700 bg-accent-100 border border-accent-200 rounded-md px-3 py-2">
                        <i className="ri-information-line mt-0.5"></i>
                        <span>
                          {isDe
                            ? `Alle drei Villen zusammen für bis zu 12 Personen – Festpreis ${money(priceMap["villa_gesamt"])} pro Nacht inklusive Frühstück für 12 Personen.`
                            : `All three villas together for up to 12 people – flat rate ${money(priceMap["villa_gesamt"])} per night including breakfast for 12 people.`}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Zimmerwahl – Frangipani & Hibiskus */}
                  {ROOM_OPTIONS[villaSlug] && (
                    <div>
                      <label className={labelClass}>
                        {isDe ? "Gewünschte Zimmer" : "Desired rooms"}
                        <span className="text-accent-600 ml-1">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {ROOM_OPTIONS[villaSlug].map((opt) => (
                          <button
                            key={opt.rooms}
                            type="button"
                            onClick={() => setRooms(opt.rooms)}
                            className={`px-4 py-3 rounded-md border text-sm font-medium cursor-pointer transition-colors ${
                              rooms === opt.rooms
                                ? "border-accent-500 bg-accent-100 text-accent-900"
                                : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                            }`}
                          >
                            {opt.rooms} {isDe ? "Zimmer" : opt.rooms === 1 ? "room" : "rooms"}
                            <span className="block text-xs font-normal text-foreground-500">
                              {money(priceMap[priceKeyFor(villaSlug, opt.rooms)] ?? 0)} / {isDe ? "Nacht inkl. Frühstück" : "night incl. breakfast"}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className={labelClass}>
                      {isDe ? "Reisezeitraum" : "Travel dates"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <RangeDatePicker
                      start={checkIn}
                      end={checkOut}
                      onStartChange={setCheckIn}
                      onEndChange={setCheckOut}
                      min={today}
                      max={maxDateStr}
                      startUnavailable={disabledDates}
                      startTurnover={checkInTurnoverDates}
                      endMin={checkIn || today}
                      endMax={maxCheckout}
                      endUnavailable={checkoutBookedDates}
                      endTurnover={checkOutTurnoverDates}
                      turnoverTooltip={
                        isDe
                          ? "Übergangstag — siehe Hinweis unten."
                          : "Turnover day — see note below."
                      }
                      isDe={isDe}
                      startLabel={isDe ? "Anreise" : "Check-in"}
                      endLabel={isDe ? "Abreise" : "Check-out"}
                      startPlaceholder={isDe ? "Datum wählen" : "Select date"}
                      endPlaceholder={isDe ? "Datum wählen" : "Select date"}
                    />
                    <CheckInOutNote isDe={isDe} className="mt-2" />
                    {isTurnoverDay && (
                      <p className="mt-2 flex items-start gap-2 text-xs text-accent-700 bg-accent-100 border border-accent-200 rounded-md px-3 py-2">
                        <i className="ri-information-line mt-0.5"></i>
                        <span>
                          {isDe
                            ? "Am Anreisetag reist ein anderer Gast ab — Anreise ab 15:00 Uhr möglich."
                            : "On the arrival day, another guest departs — check-in possible from 3:00 PM."}
                        </span>
                      </p>
                    )}
                    {isCheckoutTurnoverDay && (
                      <p className="mt-2 flex items-start gap-2 text-xs text-accent-700 bg-accent-100 border border-accent-200 rounded-md px-3 py-2">
                        <i className="ri-information-line mt-0.5"></i>
                        <span>
                          {isDe
                            ? "Am Abreisetag reist ein neuer Gast an — bitte bis 12:00 Uhr auschecken."
                            : "On the departure day, another guest arrives — please check out by 12:00 PM."}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Preisübersicht */}
                  <div className="bg-background-100 border border-background-200 rounded-md px-4 py-3">
                    <div className="flex items-center gap-2 text-xs text-foreground-600 mb-3">
                      <i className="ri-price-tag-3-line text-accent-600"></i>
                      <span className="font-medium">
                        {isDe ? "Preisübersicht" : "Price overview"}
                      </span>
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-center justify-between text-foreground-700">
                        <span>
                          {money(pricePerNight)} × {nights > 0 ? nights : 0}{" "}
                          {isDe
                            ? nights === 1 ? "Nacht (inkl. Frühstück)" : "Nächte (inkl. Frühstück)"
                            : nights === 1 ? "night (incl. breakfast)" : "nights (incl. breakfast)"}
                        </span>
                        <span className="font-medium text-foreground-900">
                          {money(totalPrice)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-t border-background-200 pt-2 font-semibold text-foreground-950">
                        <span>{isDe ? "Gesamtpreis" : "Total price"}</span>
                        <span>{money(totalPrice)}</span>
                      </div>
                      {nights <= 0 && (
                        <p className="text-xs text-foreground-500 pt-1">
                          {isDe
                            ? "Bitte An- und Abreise wählen, um den Gesamtpreis zu sehen."
                            : "Please select check-in and check-out to see the total price."}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    {/* Erwachsene */}
                    <div>
                      <label className={labelClass}>
                        {isDe ? "Anzahl Erwachsene" : "Number of adults"}
                        <span className="text-accent-600 ml-1">*</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setAdults((a) => Math.max(1, a - 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <input
                          type="number"
                          min={1}
                          max={maxAdults}
                          value={adults}
                          onChange={(e) => setAdults(Math.max(1, Math.min(maxAdults, parseInt(e.target.value) || 1)))}
                          className="w-20 text-center px-2 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                        />
                        <button
                          type="button"
                          onClick={() => setAdults((a) => Math.min(maxAdults, a + 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

                <p className="text-xs text-foreground-500">
                  {isDe
                    ? `Maximale Belegung: ${maxAdults} Erwachsene.`
                    : `Maximum occupancy: ${maxAdults} adults.`}
                </p>

                <h2 className={sectionTitleClass}>
                  {isDe ? "Ihre Angaben" : "Your Details"}
                </h2>
                <p className="mb-5 -mt-2 text-sm text-foreground-600">
                  {isDe
                    ? <>Bitte bei zwei verschiedenen Namen immer beide angeben.<br />Max / Erika&nbsp;&nbsp;&nbsp;&nbsp;MUSTERMANN / MÜLLER</>
                    : <>If there are two different names, please always provide both.<br />John / Jane&nbsp;&nbsp;&nbsp;&nbsp;DOE / SMITH</>}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="firstname" className={labelClass}>
                      {isDe ? "Vorname(n)" : "First name(s)"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input id="firstname" name="firstname" type="text" required className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="lastname" className={labelClass}>
                      {isDe ? "Familienname(n)" : "Last name(s)"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input id="lastname" name="lastname" type="text" required className={`${inputClass} uppercase`} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                  <div>
                    <label htmlFor="email" className={labelClass}>
                      {isDe ? "E-Mail-Adresse" : "Email address"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="sie@beispiel.de"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className={labelClass}>
                      {isDe ? "Telefon / WhatsApp" : "Phone / WhatsApp"}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input id="phone" name="phone" type="tel" required placeholder="+62 81 246 21 8000" className={inputClass} />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="nationality" className={labelClass}>
                    {isDe ? "Ihre Nationalität" : "Your nationality"}
                    <span className="text-accent-600 ml-1">*</span>
                  </label>
                  <NationalitySelect
                    id="nationality"
                    name="nationality"
                    isDe={isDe}
                    className={inputClass}
                  />
                </div>

                <div className="mt-5">
                  <label htmlFor="message" className={labelClass}>
                    {isDe ? "Ihre Nachricht / besondere Wünsche" : "Your message / special requests"}
                  </label>
                  <p className="text-xs text-foreground-500 mb-2">
                    {isDe
                      ? "Für eine Preisanfrage für Kinder bitte Anzahl und Alter angeben."
                      : "For a price quote for children, please specify number and age."}
                  </p>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder={isDe
                      ? "Anzahl & Alter der Kinder, Anreisezeit, besondere Wünsche..."
                      : "Number & age of children, arrival time, special requests..."}
                    className={`${inputClass} resize-none`}
                  />
                  <p className="text-xs text-foreground-500 mt-1">
                    {isDe ? "Maximal 500 Zeichen" : "Maximum 500 characters"}
                  </p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  className="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {/* Terms */}
                <div className="mt-6 flex items-start gap-3">
                  <input
                    id="terms"
                    type="checkbox"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400 cursor-pointer"
                  />
                  <label htmlFor="terms" className="text-sm text-foreground-700 leading-relaxed cursor-pointer">
                    {isDe ? (
                      <>
                        Ich habe die{" "}
                        <LocalizedLink
                          to="/impressum"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          AGB
                        </LocalizedLink>{" "}
                        gelesen und akzeptiere sie.
                      </>
                    ) : (
                      <>
                        I have read and accept the{" "}
                        <LocalizedLink
                          to="/impressum"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          terms and conditions
                        </LocalizedLink>
                        .
                      </>
                    )}
                    <span className="text-accent-600 ml-1">*</span>
                  </label>
                </div>
                <div className="mt-4 flex items-start gap-3">
                  <input
                    id="privacy"
                    type="checkbox"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400 cursor-pointer"
                  />
                  <label htmlFor="privacy" className="text-sm text-foreground-700 leading-relaxed cursor-pointer">
                    {isDe ? (
                      <>
                        Ich habe die{" "}
                        <LocalizedLink
                          to="/privacy"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          Datenschutzerklärung
                        </LocalizedLink>{" "}
                        gelesen und bin mit der Verarbeitung meiner Angaben zur Beantwortung der Anfrage einverstanden.
                      </>
                    ) : (
                      <>
                        I have read the{" "}
                        <LocalizedLink
                          to="/privacy"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          privacy policy
                        </LocalizedLink>{" "}
                        and agree to the processing of my data for the purpose of responding to my inquiry.
                      </>
                    )}
                    <span className="text-accent-600 ml-1">*</span>
                  </label>
                </div>

                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={status.type === "sending"}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-medium px-10 py-3.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                  >
                    {status.type === "sending" && <i className="ri-loader-4-line animate-spin"></i>}
                    {status.type === "sending"
                      ? isDe ? "Wird gesendet..." : "Sending..."
                      : isDe ? "Reservierung senden" : "Submit reservation"}
                  </button>
                  <BookingCompanyNotice isDe={isDe} />
                </div>

                {status.type === "success" && (
                  <div className="mt-6 text-sm font-medium text-primary-700 bg-primary-100 border border-primary-200 rounded-md px-4 py-3">
                    {status.msg}
                  </div>
                )}
                {status.type === "error" && (
                  <div className="mt-6 text-sm font-medium text-secondary-700 bg-secondary-100 border border-secondary-200 rounded-md px-4 py-3">
                    {status.msg}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}